import { DatePipe } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { ConfirmationService, ConfirmEventType, MenuItem, MessageService } from 'primeng/api';
import { Dialog } from 'primeng/dialog';
import { AppSettings } from '../Constant/AppSettings';
import { HolidaysService } from '../services/holidays/holidays.service';
import { UtilsService } from '../services/utils/utils.service';

interface FetchResult {
  name: string;
  date: string;
  checked: boolean;
}

@Component({
  selector: 'app-manage-holidays',
  templateUrl: './manage-holidays.component.html',
  styleUrls: ['./manage-holidays.component.css']
})
export class ManageHolidaysComponent implements OnInit {

  items: MenuItem[] = [{ label: 'Manage Holidays' }];
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/home' };

  isLoading = false;
  disabled = false;
  public innerWidth: any;

  holidays: any[] = [];
  filteredHolidays: any[] = [];

  // Year filter (above the list)
  uniqueYears: { label: string, value: number }[] = [];
  selectedListYear: number | null = null;

  // Manual add/edit dialog
  displayAddEditModal = false;
  editMode = false;
  id: any = '';
  holidayName = '';
  holidayDate!: Date;

  // Fetch-year dialog
  displayFetchModal = false;
  fetchYearOptions: { label: string, value: number }[] = [];
  selectedFetchYear!: number;
  isFetching = false;
  fetchResults: FetchResult[] = [];
  fetchAttempted = false;
  fetchBlockedMessage = '';

  constructor(
    public utils: UtilsService,
    private holidaysService: HolidaysService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.innerWidth = window.innerWidth;
    this.buildFetchYearOptions();
    this.listHolidays();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.innerWidth = window.innerWidth;
  }

  get state(): string {
    return this.utils.state;
  }

  buildFetchYearOptions() {
    const currentYear = new Date().getFullYear();
    this.fetchYearOptions = Array.from({ length: 11 }, (_, i) => {
      const year = currentYear + i;
      return { label: year.toString(), value: year };
    });
    this.selectedFetchYear = currentYear;
  }

  listHolidays() {
    this.isLoading = true;
    this.holidaysService.listHolidays().subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        this.holidays = (json.data || []).sort((a: any, b: any) => (a.date < b.date ? -1 : 1));
        this.buildYearFilter();
        this.hideProgressBar();
      },
      (error) => {
        this.showErrorToastMsg(AppSettings.ERROR_MSG);
        this.hideProgressBar();
      }
    );
  }

  buildYearFilter() {
    const years = Array.from(new Set(this.holidays.map(h => new Date(h.date).getFullYear()))).sort((a, b) => a - b);
    this.uniqueYears = years.map(y => ({ label: y.toString(), value: y }));

    if (this.selectedListYear == null || !years.includes(this.selectedListYear)) {
      const currentYear = new Date().getFullYear();
      if (years.includes(currentYear)) {
        this.selectedListYear = currentYear;
      } else {
        const nextAvailable = years.find(y => y > currentYear);
        this.selectedListYear = nextAvailable !== undefined ? nextAvailable : (years.length ? years[0] : null);
      }
    }
    this.applyYearFilter();
  }

  applyYearFilter() {
    this.filteredHolidays = this.holidays.filter(h => new Date(h.date).getFullYear() === this.selectedListYear);
  }

  // ── Manual add/edit ──────────────────────────────────────────

  showAddDialog() {
    this.clearFormData();
    this.editMode = false;
    this.displayAddEditModal = true;
  }

  openEditDialog(holiday: any) {
    this.clearFormData();
    this.editMode = true;
    this.id = holiday.id;
    this.holidayName = holiday.name;
    this.holidayDate = new Date(holiday.date);
    this.displayAddEditModal = true;
  }

  saveHoliday() {
    this.messageService.clear();
    if (this.holidayName == '' || this.holidayName == undefined) {
      this.showErrorMessage('Name is required.');
      return;
    }
    if (!this.holidayDate) {
      this.showErrorMessage('Date is required.');
      return;
    }

    this.showProgressBar();
    const payload = {
      name: this.holidayName,
      date: this.datePipe.transform(this.holidayDate, 'yyyy-MM-ddT00:00:00')
    };

    const request = this.editMode
      ? this.holidaysService.editHoliday(payload, this.id)
      : this.holidaysService.addHoliday(payload);

    request.subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response.status == 'SUCCESS') {
          this.showSuccessMessage(json.response.message);
          this.displayAddEditModal = false;
          this.listHolidays();
        } else {
          this.showErrorMessage(json.response.displayMessage);
        }
        this.hideProgressBar();
      },
      (error) => {
        this.showErrorMessage(AppSettings.ERROR_MSG);
        this.hideProgressBar();
      }
    );
  }

  confirmDeleteDialog(holiday: any) {
    this.messageService.clear();
    this.confirmationService.confirm({
      message: `Do you want to delete "${holiday.name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-trash',
      accept: () => {
        this.removeHoliday(holiday.id);
      },
      reject: (type: any) => {
        if (type === ConfirmEventType.CANCEL) {
          this.showErrorToastMsg('You have cancelled');
        }
      }
    });
  }

  removeHoliday(id: any) {
    this.holidaysService.removeHoliday(id).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response.status == 'SUCCESS') {
          this.showSuccessMessage(json.response.message);
          this.listHolidays();
        } else {
          this.showErrorToastMsg(json.response.displayMessage);
        }
      },
      (error) => {
        this.showErrorToastMsg(AppSettings.ERROR_MSG);
      }
    );
  }

  // ── Fetch-year bulk add ──────────────────────────────────────

  showFetchDialog() {
    this.buildFetchYearOptions();
    this.fetchResults = [];
    this.fetchAttempted = false;
    this.fetchBlockedMessage = '';
    this.displayFetchModal = true;
  }

  fetchForYear() {
    this.isFetching = true;
    this.fetchAttempted = true;
    this.fetchResults = [];
    this.fetchBlockedMessage = '';

    this.holidaysService.fetchYear(this.selectedFetchYear).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response.status == 'SUCCESS') {
          this.fetchResults = (json.data || []).map((h: any) => ({
            name: h.name,
            date: h.date,
            checked: true
          }));
        } else {
          this.fetchBlockedMessage = json.response.displayMessage;
        }
        this.isFetching = false;
      },
      (error) => {
        this.fetchBlockedMessage = AppSettings.ERROR_MSG;
        this.isFetching = false;
      }
    );
  }

  confirmBulkAdd() {
    const selected = this.fetchResults.filter(r => r.checked).map(r => ({ name: r.name, date: r.date }));
    if (selected.length === 0) {
      this.showErrorMessage('Select at least one holiday to add.');
      return;
    }

    this.showProgressBar();
    this.holidaysService.bulkAddHolidays(this.selectedFetchYear, selected).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response.status == 'SUCCESS') {
          this.showSuccessMessage(json.response.message);
          this.displayFetchModal = false;
          this.listHolidays();
        } else {
          this.showErrorMessage(json.response.displayMessage);
        }
        this.hideProgressBar();
      },
      (error) => {
        this.showErrorMessage(AppSettings.ERROR_MSG);
        this.hideProgressBar();
      }
    );
  }

  // ── Shared helpers ───────────────────────────────────────────

  showDialogMaximized(event: any, dialog: Dialog) {
    if (this.innerWidth <= 640) {
      dialog.maximized = true;
    }
  }

  clearFormData() {
    this.messageService.clear('errMsg');
    this.id = '';
    this.holidayName = '';
    this.holidayDate = undefined!;
  }

  showProgressBar() {
    this.messageService.clear();
    this.isLoading = true;
    this.disabled = true;
  }

  hideProgressBar() {
    this.isLoading = false;
    this.disabled = false;
  }

  showErrorMessage(msg: string) {
    this.messageService.add({ key: 'errMsg', severity: 'error', detail: msg });
  }

  showErrorToastMsg(msg: string) {
    this.messageService.add({ key: 'br', severity: 'error', detail: msg });
  }

  showSuccessMessage(msg: string) {
    this.messageService.add({ key: 'br', severity: 'success', detail: msg });
  }
}
